/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { UserAccess } from '@broadleaf/admin-components/dist/authentication';
import { selectAllSelectedItems } from '@broadleaf/admin-components/dist/collection/hooks/useCollectionState';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import type { ICollectionStateHook } from '@broadleaf/admin-components/dist/types/collection';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import { ITracking } from '@broadleaf/admin-components/dist/types/tracking';
import { IOrder } from '@broadleaf/admin-components/dist/types/oms';
import { ContextParameters } from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import useTracking from '@broadleaf/admin-components/dist/tracking/hooks/useTracking';
import {
  getInvoicesExportDownloadEndpoint,
  getInvoicesExportEndpoint,
  getInvoicesExportStatusEndpoint
} from '../../../utils/InvoicesOperationUtils';
import { ToastContainerContext } from '@broadleaf/admin-components/dist/common';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { every } from 'lodash';
import InvoiceAction from '@src/oms/InvoicesView/components/InvoiceAction';
import messages from './InvoicesExportAction.messages';
import InvoicesExportModal from '@src/oms/InvoicesView/components/InvoicesExportModal/InvoicesExportModal';
import { Export } from '@src/oms/utils/RmExportUtils';

const logger = log.getLogger(
  'oms.components.InvoicesView.ReadyToSubmitGrid.action-components.MarkInvoicesPaidAction'
);

const matchAccounts = (invoiceA, invoiceB) => {
  // TODO Could cause a problem if the org name changes, should eventually add either
  // buyer account id or buyer account number
  return (
    invoiceA.invoiceBillToOrganization === invoiceB.invoiceBillToOrganization
  );
};

const sleep = ms => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const MAX_EXPORT_WAIT_MILLISECONDS = 60000; // 60 seconds

async function waitForInvoiceExport(
  invoiceExport: Export,
  contextParams: IInvoiceContextParams
) {
  let currentExport = invoiceExport;
  const start = Date.now();
  while (
    currentExport &&
    currentExport.status !== 'FINISHED' &&
    currentExport.status !== 'ERROR' &&
    Date.now() - start < MAX_EXPORT_WAIT_MILLISECONDS
  ) {
    await sleep(200);
    const { data } = await request<Export>(
      {
        method: 'get',
        ...getInvoicesExportStatusEndpoint(currentExport.id)
      },
      contextParams
    );
    currentExport = data;
  }
  return currentExport;
}

async function downloadInvoiceExport(
  invoiceExport: Export,
  contextParams: IInvoiceContextParams
) {
  const { data } = await request<Blob>(
    {
      ...getInvoicesExportDownloadEndpoint(invoiceExport.id),
      responseType: 'blob'
    },
    contextParams
  );

  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    invoiceExport.name + '.' + invoiceExport.fileType.toLowerCase()
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
}

export const InvoicesExportAction: React.FC<InvoicesExportActionProps> = props => {
  const { actionDefinition, dispatch, metadata } = props;
  const formatMessage = useFormatMessage();
  const checkAccess = UserAccess.useCheckAccess();

  const collectionState = props.collectionState as ICollectionStateHook;
  const allSelectedItems = useMemo(() => {
    return selectAllSelectedItems(props.collectionState.state);
  }, [props.collectionState.state]);
  const singleAccountSelected =
    allSelectedItems &&
    allSelectedItems.length > 0 &&
    every(allSelectedItems, item => matchAccounts(item, allSelectedItems[0]));

  const disabled = !(allSelectedItems?.length > 0) || !singleAccountSelected;
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const contextParams = useContextParams(metadata);
  const endpoint = getInvoicesExportEndpoint();
  const toast = useContext(ToastContainerContext);

  let tooltip = '';
  if (!singleAccountSelected && allSelectedItems?.length > 1) {
    tooltip = 'Multiple accounts selected';
  }

  const exportInvoices = useCallback(
    async (config: InvoicesExportConfiguration) => {
      setLoading(true);
      try {
        const { status, data } = await request<InvoicesExportResponse>(
          { method: 'post', data: config, ...endpoint },
          contextParams
        );

        if (status !== 200) {
          toast.addToast(formatMessage(messages.error), {
            autoClose: 5000,
            closeOnClick: true,
            type: 'error'
          });
          return;
        }

        const invoicePdfsExport = await waitForInvoiceExport(
          data.invoicePdfsExport,
          contextParams
        );
        const statementOfAccountExport = await waitForInvoiceExport(
          data.statementOfAccountExport,
          contextParams
        );

        const isNotFinished =
          (config.includeInvoicePdfs &&
            invoicePdfsExport?.status !== 'FINISHED') ||
          (config.includeStatementOfAccount &&
            statementOfAccountExport?.status !== 'FINISHED');
        if (isNotFinished) {
          if (config.exportType === 'download') {
            toast.addToast(
              formatMessage(messages.invoicesExportDownloadError),
              {
                autoClose: 5000,
                closeOnClick: true,
                type: 'error'
              }
            );
          } else {
            toast.addToast(formatMessage(messages.invoicesExportEmailError), {
              autoClose: 5000,
              closeOnClick: true,
              type: 'error'
            });
          }
        } else if (config.exportType === 'download') {
          if (config.includeInvoicePdfs) {
            await downloadInvoiceExport(invoicePdfsExport, contextParams);
          }
          if (config.includeStatementOfAccount) {
            await downloadInvoiceExport(
              statementOfAccountExport,
              contextParams
            );
          }

          toast.addToast(
            formatMessage(messages.invoicesExportDownloadSuccessful),
            {
              autoClose: 5000,
              closeOnClick: true,
              type: 'success'
            }
          );
        } else if (config.exportType === 'email') {
          toast.addToast(
            formatMessage(messages.invoicesExportEmailSuccessful),
            {
              autoClose: 5000,
              closeOnClick: true,
              type: 'success'
            }
          );
        }

        collectionState.handleClearAllSelections();
        setLoading(false);
        setIsOpen(false);
      } catch (err) {
        if (err.cancelled) {
          return;
        }
        const errorMessage = `An error occurred while exporting invoices: ${err.message}`;
        logger.error(errorMessage);
        toast.addToast(errorMessage, {
          autoClose: 5000,
          closeOnClick: true,
          type: 'error'
        });
        setLoading(false);
        setIsOpen(false);
      }
    },
    [
      allSelectedItems,
      endpoint,
      contextParams,
      collectionState,
      toast,
      formatMessage
    ]
  );

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  }

  if (!checkAccess('INVOICE', 'UPDATE')) {
    // must have access to export
    return null;
  }

  return (
    <>
      <InvoiceAction
        color="primary"
        disabled={disabled || loading}
        onClick={handleClick}
        tooltip={tooltip}
      >
        {props.actionDefinition.label}
      </InvoiceAction>
      {isOpen && singleAccountSelected && (
        <InvoicesExportModal
          metadata={metadata}
          allSelectedItems={allSelectedItems}
          exportInvoices={exportInvoices}
          isSubmitting={loading}
          onClose={() => {
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
};

function useContextParams(metadata: IMetadata): IInvoiceContextParams {
  const { id } = useParams() as Record<string, any>;
  const tracking = useTracking(metadata);
  return useMemo(() => {
    const contextParams = {} as IInvoiceContextParams;
    if (id) {
      contextParams.id = id;
    }

    if (tracking) {
      contextParams[ContextParameters.TRACKING] = tracking;
    }
    return contextParams;
  }, [id, tracking]);
}

export interface IInvoiceContextParams {
  tracking: ITracking;
  id?: string;
  parent?: IOrder;
  order?: IOrder;
  orderId?: string;
}

export interface InvoicesExportActionProps {
  actionDefinition?: IMetadataAction;
  collectionState?: ICollectionStateHook;
  isSelectAll?: boolean;
  dispatch: Function;
  metadata: IMetadata;
  refreshUntilUpdated?: (items: any[]) => void;
}

export interface InvoicesExportConfiguration {
  accountId: string;
  customerId: string;
  emailNotes: string;
  exampleOrderId: string;
  exportType: 'download' | 'email';
  includeInvoicePdfs: boolean;
  includeStatementOfAccount: boolean;
  includePaymentInstructions: boolean;
  invoiceIds: string[];
  recipients: Array<InvoicesExportRecipient>;
}

export interface InvoicesExportResponse {
  invoicePdfsExport: Export;
  statementOfAccountExport: Export;
}

export interface InvoicesExportRecipient {
  name: string;
  email: string;
  recipientCategory: 'to' | 'cc' | 'bcc';
}

export default InvoicesExportAction;
